import React, { useState } from 'react';
import { MyActiveMicrobesResponse } from '@viome/viome-base/src/usecase/Health/Health.model';
import { StandardContainer } from '@design-system/StandardContainer';
import { NavBar } from '@design-system/NavBar';
import { Skeleton } from '@design-system/Skeleton';
import { ToggleGroup, ToggleGroupItem } from '@design-system/ToggleGroup';
import { ScrollArea, ScrollBar } from '@design-system/ScrollArea';
import { Separator } from '@design-system/Separator';
import { EventTracker } from '@/utils';

interface ActiveMicrobesProps {
  activeMicrobes?: MyActiveMicrobesResponse;
  isLoading?: boolean;
}

export const ActiveMicrobesContainer: React.FC<ActiveMicrobesProps> = ({
  activeMicrobes,
  isLoading = false,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(
    undefined,
  );

  const title = activeMicrobes?.title || 'Loading...';
  const about = activeMicrobes?.about || 'Loading description...';
  const microbes = activeMicrobes?.active_microbes || [];

  const categories = Array.from(
    new Set(microbes.map((microbe) => microbe.super_kingdom)),
  );

  const filteredMicrobes = selectedCategory
    ? microbes.filter((microbe) => microbe.super_kingdom === selectedCategory)
    : microbes;

  const renderCategoryOptions = () => {
    if (isLoading) {
      return Array.from({ length: 4 }).map((_, index) => (
        <Skeleton
          key={`skeleton-category-${index}`}
          className="w-28 h-8 mx-1 my-2"
        />
      ));
    }

    if (!categories.length) {
      return <p className="typography-caption text-secondary">No categories</p>;
    }

    return categories.map((category) => (
      <ToggleGroupItem
        key={category}
        value={category}
        className="px-4 py-2 bg-[#42434D73]"
      >
        {category}
      </ToggleGroupItem>
    ));
  };

  const renderMicrobes = () => {
    if (isLoading) {
      return Array.from({ length: 6 }).map((_, index) => (
        <Skeleton
          key={`skeleton-microbe-${index}`}
          className="w-full h-20 rounded-lg"
        />
      ));
    }

    if (!filteredMicrobes.length) {
      return (
        <p className="typography-body text-secondary">
          No microbes found in the selected category.
        </p>
      );
    }

    return filteredMicrobes.map((microbe, index) => (
      <div
        key={`${microbe.name}-${index}`}
        className="p-4 rounded-lg shadow-md"
      >
        <p className="dark typography-btn-large text-primary line-clamp-2">
          {microbe.name}
        </p>
        <p className="dark typography-caption1 text-secondary mt-2">
          {microbe.super_kingdom}
        </p>
        <Separator className="border-stroke-primary mt-4" />
      </div>
    ));
  };

  return (
    <div className="min-h-screen">
      <StandardContainer>
        <NavBar returnButtonTitle="Health" className="md:-ml-5 mt-12" />

        {/* Page Header */}
        <div className="mt-12">
          {isLoading ? (
            <div className="mb-6">
              <Skeleton className="w-3/4 h-2 mb-4" />
              <Skeleton className="w-full h-6 max-w-[37rem]" />
            </div>
          ) : (
            <>
              <h1 className="dark typography-h1 text-primary mb-4">{title}</h1>
              <p className="dark typography-body text-secondary max-w-[37rem] mb-6">
                {about}
              </p>
            </>
          )}

          {/* Category Selector */}
          <ScrollArea>
            <ToggleGroup
              type="single"
              variant="outline"
              value={selectedCategory}
              onValueChange={(data) => {
                EventTracker.trackEvent(
                  'health',
                  `microbes_filter_${selectedCategory?.toLocaleLowerCase()}`,
                );
                setSelectedCategory(data);
              }}
              className="dark flex-nowrap whitespace-nowrap"
            >
              {renderCategoryOptions()}
            </ToggleGroup>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>

          {/* Microbes Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 mt-6">
            {renderMicrobes()}
          </div>
        </div>
      </StandardContainer>
    </div>
  );
};
